import { cva } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import type { ChildrenProps, ClassNameProps } from "../types";
import { cn } from "../utils";
import { MenuContext } from "./context";
import { MenuButton } from "./menu-button";
import type { MenuItemType } from "./menu-item";
import { MenuOptions } from "./menu-options";
import { getTypeComponent, getTypeRootComponent, type MenuType } from "./shared";

/** Props Interface */
export type MenuItems = Array<MenuItemType | string | "divider" | null>;

export interface MenuProps extends ChildrenProps, ClassNameProps, HTMLAttributes<HTMLDivElement> {
	items: () => MenuItems;
	type?: MenuType;
	onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	onDoubleClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	maxHeight?: number;
	keepOpen?: boolean;
	side?: "top" | "bottom" | "left" | "right";
	align?: "start" | "center" | "end";
	isDisabled?: boolean;
	triggerAsChild?: boolean;
}

function Menu({
	children,
	items,
	type = "menu",
	className,
	maxHeight,
	side = "bottom",
	align = "end",
	isDisabled,
	triggerAsChild = true,
}: MenuProps) {
	/** Computed */
	const TypeComponent = getTypeComponent(type);
	const RootComponent = getTypeRootComponent(type);
	const triggerClasses = trigger({ type });

	/** Render */
	return (
		<MenuContext.Provider value={{ type, TypeComponent, side, align, SubMenu: MenuOptions }}>
			<RootComponent modal={false}>
				<TypeComponent.Trigger
					className={cn(triggerClasses, className)}
					asChild={triggerAsChild}
					disabled={isDisabled}
				>
					{children}
				</TypeComponent.Trigger>
				<TypeComponent.Portal>
					<MenuOptions
						items={items()}
						type="root"
						maxHeight={maxHeight}
					/>
				</TypeComponent.Portal>
			</RootComponent>
		</MenuContext.Provider>
	);
}

Menu.Button = MenuButton;

const trigger = cva([], {
	variants: {
		type: {
			menu: ["cursor-pointer"],
			"context-menu": [""],
			"menu-bar": ["cursor-default"],
		},
	},
	defaultVariants: {
		type: "menu",
	},
});

export { Menu };
